import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ElMessage } from 'element-plus';
import type { ApiResponse } from '../types/models';
import { isUuid, newUuid } from '../utils/idempotency';
import { recordCredentialMessageHit, sanitizeDisplayMessage } from './messageSanitizer';

/**
 * 契约口径 `DEV-BACKEND-§6.0-①（upstream step3）`：`shared/upstream/API-DEFINITION-step3-v0.3.md`（`info.version` 现值 v0.5）L42
 * `components.schemas.Envelope` 六枚键 + L106 所引的 `DEV-DOC-BACKEND §6.0-①`。
 * ⚠ 本节题「§6.0 冻结基线（前端据此关 mock）」在 ADMIN 侧承接件（`shared/02_…`＝v1.1）里命中 **0**（总控 seq=83 §四.5），
 *   具文只在全量件 ⇒ 出处改指可复算锚 `shared/_anchors/DEV-DOC-BACKEND-bpo-marketing-delivery__162915ed.md`（v1.8，节题 L285）
 *   或 `…__a9bc03ae.md`（v1.7，L267，两枚本节题逐字同）；盘上不存在 v1.6 字节态，⛔ 再写裸代次号（后端 seq=97 §四）。 ｜CITATION-PENDING（回补条件＝ADMIN 承接件把 §6.0／§6.2 具文同步进 shared ⇒ 本处出处由项目面锚改指承接件；产品 seq=107 §一）
 * - ③ `code` 为**字符串业务码**，成功固定 `"OK"`；判成功唯一依据 `code === "OK"`，全链路不出现 int（发起人 seq102 红线）。
 * - ④ HTTP 语义冻结：200 业务失败也走 200 + `code!="OK"`；401 清态；403 静默拦截；409 冲突；410 链接过期；
 *      422 业务规则拒绝；423 账号锁定；429 退避重试；**503 从 5xx 单列**（依赖不可用，不作缺陷报障）。
 * - ⑥ 幂等键 `Idempotency-Key: <uuid>`，值必须是合法 UUID；三态分别处置（缺键/非法键属**前端缺陷**，不得提示用户）。
 * - ⑦ 乐观锁 `rowversion` 读回必带写，`ROWVERSION_CONFLICT` 禁止自动覆盖或静默重试。
 * - 提示文案一律取 `message`，只在需要特殊交互时按 `code` 分支。
 */
export const ApiCode = {
  /** §6.0-③ 成功业务码固定字符串 */
  OK: 'OK',
  /* —— 跨切基线（§6.0-④）—— */
  BAD_REQUEST: 'BAD_REQUEST',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  FORBIDDEN: 'FORBIDDEN',
  DATA_SCOPE_DENIED: 'DATA_SCOPE_DENIED',
  END_BINDING_CONFLICT: 'END_BINDING_CONFLICT',
  NOT_FOUND: 'NOT_FOUND',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DEPENDENCY_UNAVAILABLE: 'DEPENDENCY_UNAVAILABLE',
  STATE_GUARD_REJECTED: 'STATE_GUARD_REJECTED',
  /* —— 前端必须按 code 分支的专项码 —— */
  /** §6.0-⑦ 乐观锁：数据被他人变更 → 刷新后由用户决定重试 */
  ROWVERSION_CONFLICT: 'ROWVERSION_CONFLICT',
  /** §6.0-⑥ 幂等三态：前两者属前端封装缺陷，不得提示用户 */
  IDEMPOTENCY_KEY_MISSING: 'IDEMPOTENCY_KEY_MISSING',
  IDEMPOTENCY_KEY_INVALID: 'IDEMPOTENCY_KEY_INVALID',
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  /** 登录失败走 HTTP 200，刻意不触发全局 401 —— 登录页须自行按 code 显示 */
  AUTH_LOGIN_FAILED: 'AUTH_LOGIN_FAILED',
  /** 423：5 次失败锁 15min —— 禁用登录按钮，继续重试只会延长锁定 */
  AUTH_ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  AUTH_CAPTCHA_INVALID: 'AUTH_CAPTCHA_INVALID',
  AUTH_SESSION_EXPIRED: 'AUTH_SESSION_EXPIRED',
  /** 403：跨端登录隔离——账号归属另一个控制台，本端不签发会话（m02355）。 */
  AUTH_END_NOT_ALLOWED: 'AUTH_END_NOT_ALLOWED',
  /** 410：导出链接过期 —— 不重试原链接，引导重走审批 */
  EXPORT_LINK_EXPIRED: 'EXPORT_LINK_EXPIRED',
  EXPORT_APPROVAL_REQUIRED: 'EXPORT_APPROVAL_REQUIRED',
  /**
   * S-10（seq372 §二）：这里**没有**批量条目级守卫码。后端 `ba9cfb7` 已删 `BATCH_ITEM_STATUS_GUARD`，
   * `items[].code` 从来就是该条守卫真实返回的具体码（`ORDER_NOT_FOUND` /
   * `ORDER_STATUS_ILLEGAL_TRANSITION` / `URGE_ORDER_NOT_APPLICABLE` …）。不为不存在的码留常量与分支。
   */
  ORDER_DELIVERY_METHOD_UNSUPPORTED_P0: 'ORDER_DELIVERY_METHOD_UNSUPPORTED_P0',
  /**
   * seq322 §七 D-06：`assign`/`reassign`/`pool/assign` 的 `courierId` **有值但库内无此交付员**
   * → 422 本码（业务不可执行，要提示用户换人）；**缺失/空/非正数** → 400 `VALIDATION_FAILED`
   * （请求体不合法，前端应在提交前拦截、不该发出请求）。两码不得合并处置。
   */
  COURIER_NOT_FOUND: 'COURIER_NOT_FOUND',
} as const;

/** HTTP 层语义（§6.0-④），与业务码分开，避免再次混用 int 当业务码 */
const Http = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  /** §6.0-④ 新增：导出链接已过期 */
  GONE: 410,
  /** §6.0-④ 新增：账号已锁定 */
  LOCKED: 423,
  GUARD_REJECTED: 422,
  TOO_MANY: 429,
  /** §6.0-④：从 5xx 单列，提示「稍后重试」而非报障 */
  DEPENDENCY_UNAVAILABLE: 503,
} as const;

/**
 * 契约漂移取证。后端 seq304 那轮（commit `91d8675`；其修订行见 v1.7 锚 `…__a9bc03ae.md` L5，盘上不存在 v1.6 字节态）已把统一封装重构为**字符串业务码**（`code=="OK"`）， ｜CITATION-PENDING（回补条件＝ADMIN 承接件把 §6.0／§6.2 具文同步进 shared ⇒ 本处出处由项目面锚改指承接件；产品 seq=107 §一）
 * 本文件仍保留 `int 0` / `isSuccess` 兼容分支，目的只有一个：万一打到旧部署包或发生回退时页面不白屏。
 * 命中即记一条**样本证据**（不静默容忍），供 `getContractDrift()` 导出后回报后端；
 * 业务代码一律不得依赖该兼容分支，也不得再按 int 比较业务码。
 *
 * 导出给 API 适配层复用：端点已冻结但**字段名未冻结**（如看板族）时，适配一次并在此记账。
 */
const driftSamples = new Map<string, { count: number; sample: string }>();

export function recordDrift(kind: string, observed: unknown): void {
  const hit = driftSamples.get(kind);
  if (hit) {
    hit.count += 1;
    return;
  }
  driftSamples.set(kind, { count: 1, sample: JSON.stringify(observed) });
  console.warn(`[contract-drift] ${kind} 与冻结口径不一致，实测：${JSON.stringify(observed)}`);
}

export function getContractDrift(): Array<{ kind: string; count: number; sample: string }> {
  return Array.from(driftSamples.entries()).map(([kind, v]) => ({ kind, ...v }));
}

/**
 * §6.0-③ 判成败：`code !== "OK"` 即业务失败。**唯一依据是 `resultCode`**（项负责 seq=2216 四端同批：
 * 「判成败只看 resultCode，别再拿 `code` 那枚字符串比 OK」）——`code` 由 `resultCode` 单向派生，
 * 后端下一趟（明晚换装）只**新增** `resultCode`、`code` 仍是字符串，再往后那枚才翻成 int。
 * 所以现在就把权威收到 `resultCode`：它**在位** ⇒ 它单独定成败，⛔ 再回头拿 `code` 翻案；
 * 那一步落地后 `code` 无论翻成什么形状都影响不到判据。
 *
 * ⚠ 两枚「不在位」臂都只作**过渡兜底**且必须**记账**（`recordDrift`），⛔ 读成契约形状：
 * ① 今天这台 8080 只有五键、`code` 是字符串业务码而 `resultCode` 压根没有（后端 seq=2106 认的欠账），
 *    这一臂就是它，换装当天 `resultCode` 到位后自动失效——命中次数即「还没换装」的读数；
 * ② 旧部署包 / 回退态的 int `code` 与裸 `isSuccess` 布尔，页面不白屏但漂移照记。
 *
 * ⚠ 本函数只吃**信封那一层**。批量条目里那枚 `items[].code` 是契约明写的字符串真码名（`ORDER_NOT_FOUND`
 * 之类，见 `types/models.ts:BatchItemResult`），跟信封那枚不是一回事——项负责 seq=2216 明令
 * ⛔ 写一枚通用解析同时吃两层，条目层判定一律在调用方按 `BatchItemResult` 自己读。
 */
export function isSuccessCode(code: unknown, body?: Partial<ApiResponse<unknown>>): boolean {
  const resultCode = body?.resultCode;
  if (typeof resultCode === 'string') {
    if (resultCode === ApiCode.OK) return true;
    if (resultCode.toLowerCase() === 'ok') {
      recordDrift('resultCode 非冻结大小写（应为 "OK"）', resultCode);
      return true;
    }
    return false;
  }
  if (resultCode !== undefined && resultCode !== null) {
    recordDrift('resultCode 非字符串（冻结件 Envelope 明写 string 业务码）', resultCode);
    return resultCode === 0;
  }
  if (typeof code === 'string') {
    if (code === ApiCode.OK) {
      recordDrift('信封缺 resultCode，判成败暂读 code 那枚字符串（过渡臂，换装当天自动失效）', code);
      return true;
    }
    if (code.toLowerCase() === 'ok') {
      recordDrift('code 非冻结大小写（应为 "OK"）', code);
      return true;
    }
    return false;
  }
  if (typeof code === 'number') {
    recordDrift('code 为 int 业务码（冻结契约要求明文字符串，成功固定 "OK"）', code);
    return code === 0;
  }
  if (body && typeof body.isSuccess === 'boolean') {
    recordDrift('响应缺少 code 字段，仅 isSuccess 布尔', { isSuccess: body.isSuccess });
    return body.isSuccess;
  }
  return false;
}

/**
 * §6.0-③／冻结件 `Envelope` 判形（**唯一一处**，`ApiError.isEndpointMissing` 的判据即此处）。
 *
 * 为什么只能按体形状二分，⛔ 只看状态码（授权来源：后端 seq=58 裁定 ＋ 产品负责人 seq=68 裁 1）：
 * ASP.NET Core 路由不匹配时**不落体**——本机 8080 实测 `GET /regions`、`GET /users/1` 均为
 * `404 ＋ Content-Length: 0 ＋ 无 Content-Type`；而「资源不存在」按 `Envelope` 走 **404 ＋ 信封体**
 * （`/api/orders/{id}` 的 `get.responses.404` 的 `schema.$ref = Envelope`，描述明写
 * 「resultCode ∈ {ORDER_NOT_FOUND}，信封 data=null；⛔ 读 message 判分支」）。两者状态码相同，
 * 故二分二者必须问「这是不是信封」，否则「订单不存在」会被误判成「端点未部署」。
 *
 * ⚠ 判据字段名（seq=68 裁 1 的更正项）：业务码**唯一承载位是 `resultCode`**（string），`code` 只是
 * 由它单向派生的 int 槽 ⇒ 本谓词**只认 `resultCode`**。曾考虑留一条「带 `code` 也算落体」的兼容臂，
 * 后端 seq=72 §二 以现网三面证据否决并已由前端采纳：两台实服务当前都**不产** `resultCode`
 * （.42 登录失败体 `code` 是 string、本机是 int `40100`，均 5 键无 `resultCode`），放宽等于把
 * `40100` 与 `"OK"` 两种语义压成同一信号，且违契约原文「⛔ 回退读 `code`」。
 * ⇒ 代价照实记账：后端交付六键信封（`resultCode` 到位）**之前**，现网所有真 404 都会被本谓词判成
 * 「端点未部署」，并被 `surfaceError` 一并静默（用户看不到任何报错）——这是本判据的已知残余代价，
 * ⛔ 靠本谓词自行放宽去兜。seq=72 §七.2：改对了也全判未部署，⛔ 当成前端回归失败。
 *
 * content-type 按 `/json/` 匹配而非字面 `application/json`：ASP.NET 错误面常见
 * `application/problem+json`，按字面匹配会把这种真信封判成未落体。写成类型谓词是为**保住 TS 收窄**
 * （写 `: boolean` 会让调用点把已判过封装的 `body` 继续当成可能 undefined，本轮实测退 7 枚 TS18048）。
 */
export function isUnifiedEnvelope(data: unknown, contentType?: string | null): data is ApiResponse<unknown> {
  if (typeof contentType === 'string' && contentType !== '' && !/json/i.test(contentType)) return false;
  return !!data && typeof data === 'object' && !Array.isArray(data) && 'resultCode' in data;
}

export class ApiError extends Error {
  /** 业务码（字符串为冻结口径；number 仅出现在漂移取证，新代码不得比较 int） */
  readonly code: string | number;
  /**
   * 信封里的业务码唯一载体（PRD `:55`；`:69` 之后它是**定因的唯一入口**，没有"`code` 兜底"这回事）。
   * 今日盘上缺 ⇒ 可选；本类的存在意义就是**别把它丢掉**：若这里不接，`readErrorShapeOf` 读不到
   * `resultCode` 就**不定因**（渲染走 `NO_CARRIER_COPY` 那句定版文案），#64 落地当天我们仍只能报
   * 「不可判」——而派生出来的 `code` int 化后多枚业务码会并成同一个值（产品 seq734 三防的那格）。
   */
  readonly resultCode?: string | number;
  readonly httpStatus?: number;
  readonly traceId?: string;
  readonly payload?: unknown;
  /**
   * 应答体是否为 §6.0-③ 信封。由构造点算一次（`isUnifiedEnvelope(body)`），⛔ 在本类推导：
   * 本类不持有原始 response，事后无从分辨「404 零字节」与「404 信封＋data=null」。
   * 未传＝来源不是 HTTP 应答（如 `auth.ts` 自造的形状错误），此时按「未知」处理。
   */
  readonly responseHasEnvelope?: boolean;

  constructor(
    message: string,
    code: string | number,
    httpStatus?: number,
    traceId?: string,
    payload?: unknown,
    resultCode?: string | number,
    responseHasEnvelope?: boolean,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.resultCode = resultCode;
    this.httpStatus = httpStatus;
    this.traceId = traceId;
    this.payload = payload;
    this.responseHasEnvelope = responseHasEnvelope;
  }

  /**
   * ⚠ 只比 `this.code`。#64／v1.8 把 `code` int 化成 HTTP 状态之后，下面这组按字符串业务码推导的
   * getter 会**集体失配**——那是 v1.8 迁移批（RK18-2）要与后端同批改的账，⛔ 在本格提前改语义
   * （今日盘上无 `resultCode`，改了就是把一个不存在的形状当成线上事实）。
   */
  private is(code: string): boolean {
    return this.code === code;
  }

  /** §6.0-⑦ 乐观锁冲突：与幂等/抢单/已派单同处 409，但文案与处置不同，必须按 code 分支 */
  get isRowversionConflict(): boolean {
    return this.is(ApiCode.ROWVERSION_CONFLICT);
  }

  /** seq322 §七 D-06 的 422 分支：所选交付员库内不存在 → 提示换人，不是表单校验缺陷 */
  get isCourierNotFound(): boolean {
    return this.is(ApiCode.COURIER_NOT_FOUND);
  }

  /**
   * §6.0-⑥ + seq322 §五 裁定（v1.8 权威口径）：**同键 + 同 payload → 200 `OK` + 原结果**（含原
   * `orderNo`），是弱网重试/重复点击的透明重放，走成功分支、无需二次查询；
   * **同键 + 不同 payload → 409 `IDEMPOTENCY_KEY_REUSED`** 才是这里命中的分支 —— 键空间污染，
   * 必须显式拒绝。前端**绝不可**为此自动换新键重发：那正是绕过幂等、直接造出重复订单的路径。
   */
  get isIdempotencyReused(): boolean {
    return this.is(ApiCode.IDEMPOTENCY_KEY_REUSED);
  }

  /** §6.0-⑥ 缺键 / 键非 UUID：属**前端封装缺陷**，不得提示用户，只记证据 + console.error */
  get isIdempotencyFrontendDefect(): boolean {
    return this.is(ApiCode.IDEMPOTENCY_KEY_MISSING) || this.is(ApiCode.IDEMPOTENCY_KEY_INVALID);
  }

  /** 409 族（乐观锁 / 幂等 / 抢单竞争 / 已派单 / 配置版本冲突）：一律不自动覆盖 */
  get isConflict(): boolean {
    return this.httpStatus === Http.CONFLICT;
  }

  get isUnauthorized(): boolean {
    return this.httpStatus === Http.UNAUTHORIZED || this.is(ApiCode.UNAUTHENTICATED) || this.is(ApiCode.TOKEN_EXPIRED);
  }

  /** §6.0-④ 403：已鉴权无权限（数据范围越权、端绑定互斥、导出未审批、审计不可改）——静默拦截、不重试 */
  get isForbidden(): boolean {
    return (
      this.httpStatus === Http.FORBIDDEN ||
      this.is(ApiCode.FORBIDDEN) ||
      this.is(ApiCode.DATA_SCOPE_DENIED) ||
      this.is(ApiCode.END_BINDING_CONFLICT) ||
      this.is(ApiCode.EXPORT_APPROVAL_REQUIRED)
    );
  }

  /** §6.0-④ 422：状态机守卫 / 业务规则拒绝（十余个码共用一档，文案取 message） */
  get isGuardRejected(): boolean {
    return this.httpStatus === Http.GUARD_REJECTED;
  }

  /** 423 账号锁定：禁用登录按钮，继续重试只会延长锁定 */
  get isAccountLocked(): boolean {
    return this.httpStatus === Http.LOCKED || this.is(ApiCode.AUTH_ACCOUNT_LOCKED);
  }

  /** 410 导出链接过期：不重试原链接 */
  get isExportLinkExpired(): boolean {
    return this.httpStatus === Http.GONE || this.is(ApiCode.EXPORT_LINK_EXPIRED);
  }

  /** 503 依赖不可用：从 5xx 单列，不作缺陷报障 */
  get isDependencyUnavailable(): boolean {
    return this.httpStatus === Http.DEPENDENCY_UNAVAILABLE || this.is(ApiCode.DEPENDENCY_UNAVAILABLE);
  }

  /**
   * 端点未部署（§6.2 缺口）——区别于「资源不存在」，供取证与静默判定。
   *
   * 判据＝404/405 **且** 应答体不是信封（后端 seq=58 裁定）。只按状态码判会两头错：
   * 本机 step2 构建上 `/regions` 一类是「路由未命中＋零字节体」，而 gap 构建上
   * `ORDER_NOT_FOUND` 是「404＋信封」（真业务事实，必须原样呈现）。本 getter 同时被
   * `surfaceError` 用作「静默不弹提示」的条件，误判的代价是**用户看不到任何报错**。真实构造点
   * （错误拦截器）已显式传入判形结果；只有拿不到证据时才退回旧的「只比状态码」口径——翻案需要**正向证据**
   * （确有其信封），⛔ 靠"缺证据"顺手把本判别量之外的既有 404 处置也一起改掉。
   * 残余风险（后端 seq=58 三自陈，未闭合）：GUID 形态 id 打在已部署的 `/orders/{id}` 上同样产生
   * 「404＋零字节」，与路由未命中同形 ⇒ 前端必须按契约把 id 传成 int64，⛔ 指望本判据兜住传参类型错。
   */
  get isEndpointMissing(): boolean {
    const routingMiss = this.httpStatus === Http.NOT_FOUND || this.httpStatus === Http.METHOD_NOT_ALLOWED;
    return routingMiss && this.responseHasEnvelope !== true;
  }

  /**
   * 面向用户的文案：冻结口径要求 5xx 携带 traceId 报障。
   *
   * ⚠ 本 getter 同时是**凭据回显的唯一显示出口**（产品负责人 seq=86 §六.2，P1）：末尾过一道
   * `sanitizeDisplayMessage`，命中就换 `●●●` 并记一条 `credential-in-message` 计数。三条边界都在这格：
   * ⛔ 动判定（分支照旧读 `this.message`／业务码）、⛔ 动日志（`console.error` 那条留原文，
   * 否则后端拿不到复现串）、⛔ 因命中而**不弹**（弹脱敏后的文案——静默会让用户以为操作没发生）。
   */
  get displayMessage(): string {
    const base = this.message || '请求失败';
    const withTrace = (text: string): string => (this.traceId ? `${text}（traceId: ${this.traceId}）` : text);
    let text: string;
    if (this.isRowversionConflict) text = withTrace(`${base}｜数据已被他人变更，请刷新后重试`);
    else if (this.isIdempotencyReused) text = withTrace('该提交键已被另一笔内容占用，本次未被执行；请返回上一步核对内容后重新发起，不要更换新键重录（会产生重复单据）');
    else if (this.isAccountLocked) text = withTrace(`${base}｜锁定期间继续尝试只会延长时间`);
    else if (this.isExportLinkExpired) text = withTrace('下载链接已过期，请重新发起导出审批');
    else if (this.isDependencyUnavailable) text = withTrace('服务依赖暂不可用，请稍后重试');
    else text = withTrace(base);
    return maskCredentialForDisplay(text, this);
  }
}

/**
 * 显示层脱敏一次 ＋ 命中计数（seq=86 §六.2 的落点，⛔ 在别处再调一次——那会长出第二个计数面）。
 *
 * 去重用 `WeakSet`：`displayMessage` 是 getter，组件重渲染就会再读一遍，⛔ 把一条泄漏数成十段。
 * 业务码取 `resultCode` 优先（seq=68 裁 1：它是唯一承载位，`code` 只是派生槽），读数只为定位"哪一枚接口
 * 在回显"，⛔ 拿它判分支。
 */
const credentialCounted = new WeakSet<object>();

function maskCredentialForDisplay(text: string, err: ApiError): string {
  const { text: safe, rule } = sanitizeDisplayMessage(text);
  if (!rule) return safe;
  if (credentialCounted.has(err)) return safe;
  credentialCounted.add(err);
  recordCredentialMessageHit(err.resultCode ?? err.code, rule);
  return safe;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** 写操作幂等键，由请求头 Idempotency-Key 承载（§6.0-⑥） */
    idempotencyKey?: string;
    /** 跳过鉴权头（登录/验证码） */
    skipAuth?: boolean;
    /** 网络错误/5xx/429 自动重试次数（**仅只读方法生效**；写操作受 S-13 禁令一律不自动重试） */
    retry?: number;
    /** 静默模式：不弹全局错误提示，由调用方处理 */
    silent?: boolean;
  }
}

export interface TokenProvider {
  getAccessToken(): string | null;
  onUnauthorized(): void;
}

let tokenProvider: TokenProvider = {
  getAccessToken: () => null,
  onUnauthorized: () => undefined,
};

export function bindTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

const END = import.meta.env.VITE_APP_END || 'ADMIN';

const READONLY_METHODS = new Set(['get', 'head', 'options']);

/**
 * S-13 落地前的写操作重试禁令（seq340）：服务端对 `Idempotency-Key` 当前只做
 * 「存在性 + UUID 格式」校验，**既无登记表比对也无结果缓存** ⇒ 「带同一个键重放是安全的」
 * 这个前提为假，网络抖动重试 / 超时重发会真实改坏数据。所以只读方法保留退避重试，
 * 写操作一律不自动重试；网络抖动、超时、401 刷新后重放三条自动路径全部关闭。
 * 用户**手动**再点一次按 seq322 §五 C-05 第一行走「同键重放」语义（`withIdempotency` 按业务
 * 意图复用同一 UUID，不换键），但同键重放要拿回原结果仍取决于 S-13 的登记表，故视图层另加
 * 「一次业务意图只允许一条在途写」的单飞保护。
 */
function shouldRetry(config: InternalAxiosRequestConfig & { __retryCount?: number }): boolean {
  const method = (config.method || 'get').toLowerCase();
  if (!READONLY_METHODS.has(method)) {
    if (config.retry) {
      recordDrift('S-13 未落地前写操作不得自动重试（调用方显式请求的重试已就地禁用）', `${method.toUpperCase()} ${config.url ?? ''}`);
    }
    return false;
  }
  const max = config.retry ?? (method === 'get' ? 2 : 0);
  const done = config.__retryCount ?? 0;
  return done < max;
}

function backoff(attempt: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 220 * (attempt + 1)));
}

/**
 * 统一的用户侧错误处置策略（§6.0-④）。
 * HTTP 非 2xx 与「HTTP 200 + code !== "OK"」两条路必须共用同一个入口：axios 的 fulfilled
 * 分支内 throw 不会被同一拦截器的 rejected 分支捕获，若只在 rejected 分支提示，
 * 状态守卫、导出链接过期、依赖不可用等 200 业务失败会全部静默——用户看不到任何反馈。
 */
function surfaceError(apiError: ApiError, config?: InternalAxiosRequestConfig): void {
  if (apiError.isUnauthorized) {
    tokenProvider.onUnauthorized();
  }

  if (apiError.isIdempotencyFrontendDefect) {
    // §6.0-⑥：缺键 / 键非法属前端自身缺陷，**不得提示用户**；保留开发侧证据便于定位调用点
    console.error(
      `[idempotency-defect] ${apiError.code} ${config?.method?.toUpperCase() ?? ''} ${config?.url ?? ''} traceId=${apiError.traceId ?? '-'}`,
    );
    return;
  }

  // silent 由调用方自渲染（如登录页需按码给文案）；403 静默拦截不重试；404/405 端点缺失（路由未命中＋无信封）同样静默，由调用方按 httpStatus 给文案
  if (config?.silent || apiError.isForbidden || apiError.isEndpointMissing) return;

  // §七 D-06 的 422 分支：契约要求「告知用户换人」，通用文案无法承载这个动作，
  // 故全局静默，由分配弹窗就地清空人选并给出可操作提示（与 400 的行内红框分路）。
  if (apiError.isCourierNotFound) return;

  ElMessage({
    type: apiError.isConflict || apiError.isGuardRejected ? 'warning' : 'error',
    message: apiError.displayMessage,
  });
}

export interface HttpClientOptions {
  adapter?: AxiosRequestConfig['adapter'];
  /** §6.0-①：base 即后端根（`http://<backend-host>:<port>` 或同源 ''），**不得拼 /api** */
  baseURL?: string;
}

/**
 * §6.0-③ 序列化约定实测补充：后端主键为 `long`，线上是 JSON **数字**，而前端模型统一以
 * **字符串**承载 id（见 `utils/contract.ts`）。不在出口归一，就会出现
 * 「路由参数 `'12'` === 响应里的 `12`」恒假的静默失配（详情跳转会白屏一类的根因）。
 * 只处理 `id` / `*Id` / `*Ids` 三种命名，其它数值（fee、counts、permVersion…）原样保留。
 */
function isIdKey(key: string): boolean {
  return key === 'id' || key.endsWith('Id') || key.endsWith('Ids');
}

export function stringifyWireIds<T>(value: T, depth = 0): T {
  if (depth > 12 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => stringifyWireIds(item, depth + 1)) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!isIdKey(key)) {
      out[key] = stringifyWireIds(raw, depth + 1);
      continue;
    }
    // 超出安全整数的 long 在 JSON.parse 阶段已丢精度，这里只做类型归一，不假装能恢复
    out[key] = Array.isArray(raw)
      ? raw.map((item) => (typeof item === 'number' ? String(item) : stringifyWireIds(item, depth + 1)))
      : typeof raw === 'number'
        ? String(raw)
        : stringifyWireIds(raw, depth + 1);
  }
  return out as unknown as T;
}

export function createHttpClient(options: HttpClientOptions = {}): AxiosInstance {
  const baseURL = options.baseURL ?? import.meta.env.VITE_API_BASE_URL ?? '';

  const client = axios.create({
    baseURL,
    timeout: 20000,
    headers: { 'Content-Type': 'application/json' },
    adapter: options.adapter,
    // COORD-RULINGS §I 终态：鉴权只走 `Authorization: Bearer`、不依赖 Cookie，凭证模式与
    // 令牌头架构本就互斥，故显式写死 false（防后来者为联调顺手打开）。§I 已撤回
    // 「ACAO:* + credentials 会被浏览器拒」那条推断，本值不以任何 CORS 报错为依据——
    // **前端不为 CORS 做任何改动**，跨域归部署侧与 dev 代理负责。
    withCredentials: false,
  });

  client.interceptors.request.use((config) => {
    const token = tokenProvider.getAccessToken();
    if (token && !config.skipAuth) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    config.headers.set('X-Client-End', END);

    // §6.0-⑥：写操作一律携带合法 UUID 幂等键。缺键 → 409 MISSING、非 UUID → 400 INVALID，
    // 两者都被契约判定为**前端封装缺陷**（不得提示用户），故在出口兜底生成/纠正；
    // 调用方传入的非法键不静默吞掉——记一条漂移证据，便于回查修调用点。
    const method = (config.method || 'get').toLowerCase();
    const isWrite = method !== 'get' && method !== 'head' && method !== 'options';
    if (isWrite) {
      const provided = config.idempotencyKey;
      if (provided && !isUuid(provided)) {
        recordDrift('调用方传入的 Idempotency-Key 非 UUID（已就地纠正，请修调用点）', provided);
      }
      const key = provided && isUuid(provided) ? provided : newUuid();
      config.idempotencyKey = key;
      config.headers.set('Idempotency-Key', key);
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      // §6.0-③ 例外：**文件流腿**（调用方显式声明 `responseType: 'blob'`／`'arraybuffer'`，如报表导出
      // 产物下载 GET /api/reports/export/{taskId}/download）的应答体本就是真文件字节，后端该路由无
      // `.Produces`、200 无六键信封（OffContractAnnotations.cs:1485-1487）⇒ **不是契约漂移**，
      // 原样透传且⛔ 不记漂移（否则每下一次产成就刷一条假的「响应缺少封装」证据）。
      // 失败臂（该下载口的裸 404/410）照旧走下面 rejected 分支，由调用方按 httpStatus 分词。
      const responseType = response.config?.responseType;
      if (responseType === 'blob' || responseType === 'arraybuffer') return response;
      const body = response.data as ApiResponse<unknown> | undefined;
      const contentType = (response.headers?.['content-type'] ?? null) as string | null;
      // 非统一封装（文件流、后端裸 404 页）直接透传
      if (!isUnifiedEnvelope(body, contentType)) {
        if (response.status >= Http.BAD_REQUEST) {
          throw new ApiError('读取失败：服务端返回内容无法识别，请稍后重试', response.status, response.status, undefined, undefined, undefined, false);
        }
        recordDrift('响应缺少 {code,message,data,traceId} 封装', { status: response.status });
        return response;
      }
      // 实测：部分 §6.2 端点连 `traceId` 键都不写（不只是 null），一律记漂移
      if (body.traceId === null || body.traceId === undefined || body.traceId === '') {
        recordDrift('traceId 未透传（冻结契约要求全链路透传，前端无法据此报障）', { present: 'traceId' in body });
      }
      if (!isSuccessCode(body.code, body)) {
        const apiError = new ApiError(
          body.message || '请求失败',
          body.code,
          response.status,
          body.traceId ?? undefined,
          body.data,
          body.resultCode,
        );
        surfaceError(apiError, response.config);
        throw apiError;
      }
      response.data = stringifyWireIds(body.data);
      return response;
    },
    async (error: AxiosError<ApiResponse<unknown>>) => {
      const config = error.config as (InternalAxiosRequestConfig & { __retryCount?: number }) | undefined;
      const status = error.response?.status;
      const body = error.response?.data;

      // §6.0-④：429 与网络错误/5xx 走退避重试——S-13 落地前**只对只读方法生效**，见 shouldRetry
      const retriable = !error.response || status === Http.TOO_MANY || (status ?? 0) >= 500;
      if (config && retriable && shouldRetry(config)) {
        config.__retryCount = (config.__retryCount ?? 0) + 1;
        await backoff(config.__retryCount - 1);
        return client.request(config);
      }

      const apiError = new ApiError(
        body?.message || error.message || '网络异常，请稍后重试',
        body?.code ?? status ?? ApiCode.INTERNAL_ERROR,
        status,
        body?.traceId ?? undefined,
        body?.data,
        body?.resultCode,
        isUnifiedEnvelope(body, (error.response?.headers?.['content-type'] ?? null) as string | null),
      );

      surfaceError(apiError, config);
      return Promise.reject(apiError);
    },
  );

  return client;
}

/** §6.0-⑤：分页 size 默认 20、上限 200 */
export const PAGE_SIZE_MAX = 200;
export const PAGE_SIZE_DEFAULT = 20;

export function clampPageSize(size?: number): number {
  const n = Math.floor(Number(size ?? PAGE_SIZE_DEFAULT));
  if (!Number.isFinite(n) || n < 1) return PAGE_SIZE_DEFAULT;
  return Math.min(n, PAGE_SIZE_MAX);
}

/** §6.0-⑤ 排序子句：`{ field, dir }`，多组按数组顺序传递 */
export interface SortClause {
  field: string;
  dir: 'asc' | 'desc';
}

/**
 * §6.0-⑤：排序参数 `sort=field,asc|desc`，**可多组、逗号分隔**（具文见 v1.7 锚 `…__a9bc03ae.md` L376）， ｜CITATION-PENDING（回补条件＝ADMIN 承接件把 §6.0／§6.2 具文同步进 shared ⇒ 本处出处由项目面锚改指承接件；产品 seq=107 §一）
 * 即 `sort=createdAt,desc,orderNo,asc`。不得使用重复 sort 参数，也不得自创其它分隔符。
 */
export function buildSort(clauses: SortClause | SortClause[]): string | undefined {
  const list = (Array.isArray(clauses) ? clauses : [clauses]).filter((item) => item?.field);
  if (list.length === 0) return undefined;
  return list.map((item) => `${item.field},${item.dir === 'asc' ? 'asc' : 'desc'}`).join(',');
}

/** 统一取数：返回 ApiResponse.data（响应拦截器已解封装） */
export async function unwrap<T>(promise: Promise<{ data: T }>): Promise<T> {
  const res = await promise;
  return res.data;
}
